/**
 * Home-page persona toggle: which home dashboard `/` redirects to.
 *
 * Per-device preference (localStorage). A convenience toggle, not a security
 * gate — every home page remains accessible to every user with permission
 * for the underlying endpoints.
 *
 * Role defaults when no preference has been set:
 *   sales_rep                 → sales
 *   csr                       → csr
 *   manager / admin           → ops
 *   finance                   → finance
 *   anything else (marketing, pending) → sales
 */
import { useCallback, useEffect, useState } from "react";

import { usePlatformCapabilities } from "../capabilities/context";
import { paths, type RouteId } from "../routing/routes";

export const HOME_PERSONA_STORAGE_KEY = "cis.home_persona";

export type HomePersona = "sales" | "csr" | "ops" | "finance";

/**
 * Each persona's home page — the one place that mapping lives, so the
 * redirect, the account menu and every home page's toggle cannot disagree.
 */
export const HOME_PERSONAS: {
  persona: HomePersona;
  label: string;
  route: RouteId;
  path: string;
}[] = [
  {
    persona: "sales",
    label: "Sales",
    route: "homeSales",
    path: paths.homeSales(),
  },
  { persona: "csr", label: "CSR/AM", route: "homeCsr", path: paths.homeCsr() },
  { persona: "ops", label: "Ops", route: "homeOps", path: paths.homeOps() },
  {
    persona: "finance",
    label: "Finance",
    route: "homeFinance",
    path: paths.homeFinance(),
  },
];

export function homePathFor(persona: HomePersona): string {
  return HOME_PERSONAS.find((p) => p.persona === persona)!.path;
}

const VALID_PERSONAS = new Set<HomePersona>(
  HOME_PERSONAS.map((p) => p.persona),
);

export function defaultPersonaForRole(
  role: string | null | undefined,
): HomePersona {
  if (role === "sales_rep") return "sales";
  if (role === "csr") return "csr";
  if (role === "manager" || role === "admin") return "ops";
  if (role === "finance") return "finance";
  return "sales";
}

function readStoredPersona(value: string | null): HomePersona | null {
  if (value === null) return null;
  return VALID_PERSONAS.has(value as HomePersona)
    ? (value as HomePersona)
    : null;
}

interface UseHomePersonaOptions {
  role: string | null | undefined;
}

interface UseHomePersonaResult {
  /** Effective persona — stored preference, falling back to role-default. */
  persona: HomePersona;
  /** Update the persona. ``null`` clears the stored override. */
  setPersona: (next: HomePersona | null) => void;
}

export function useHomePersona({
  role,
}: UseHomePersonaOptions): UseHomePersonaResult {
  const capabilities = usePlatformCapabilities();
  const [stored, setStored] = useState<HomePersona | null>(() =>
    readStoredPersona(capabilities.getStorageItem(HOME_PERSONA_STORAGE_KEY)),
  );

  // Keep state in sync if storage changes from elsewhere (e.g. another tab).
  useEffect(() => {
    setStored(
      readStoredPersona(capabilities.getStorageItem(HOME_PERSONA_STORAGE_KEY)),
    );
  }, [capabilities]);

  const setPersona = useCallback(
    (next: HomePersona | null) => {
      capabilities.setStorageItem(HOME_PERSONA_STORAGE_KEY, next);
      setStored(next);
    },
    [capabilities],
  );

  const persona = stored ?? defaultPersonaForRole(role);
  return { persona, setPersona };
}
